import { useGenerationRealtime } from '@/hooks/use-generation-realtime';
import { env } from '@/lib/env';
import { toast } from 'sonner';
import { mutate } from 'swr';
import { reuseOrAppendHistory } from '@/components/nodes/shared/media-history';

type TranslateFn = (key: string, vars?: Record<string, unknown>) => string;

export type UseAudioRealtimeParams = {
  projectId?: string | null;
  nodeId: string;
  data: any;
  modelId: string;
  updateNodeData: (id: string, partial: any) => void;
  getNode: (id: string) => any;
  t: TranslateFn;
};

export function useAudioRealtime({ projectId, nodeId, data, modelId, updateNodeData, getNode, t }: UseAudioRealtimeParams) {
  const isRealtimeEnabled =
    String(env.NEXT_PUBLIC_ENABLE_REALTIME ?? 'true') !== 'false' &&
    Boolean(projectId) &&
    (data as any)?.status === 'processing';

  useGenerationRealtime({
    projectId: projectId ?? '',
    nodeId,
    generationId: (data as any)?.processingGenerationId as string | undefined,
    enabled: isRealtimeEnabled,
    onCompleted: (url, metadata) => {
      const nowIso = new Date().toISOString();
      const responseMeta = (metadata as any)?.response ?? {};
      const inferredType = ((metadata as any)?.type as string | undefined) || (responseMeta?.type as string | undefined) || 'audio/mpeg';
      const lyrics = (metadata as any)?.lyrics ?? responseMeta?.lyrics ?? undefined;

      const urls: string[] = Array.isArray(responseMeta?.urls)
        ? responseMeta.urls.filter(Boolean)
        : (typeof url === 'string' && url ? [url] : []);

      const latestNode = (() => { try { return getNode(nodeId); } catch { return undefined; } })();
      const latestData = (latestNode as any)?.data ?? {};

      const { history: nextHistory, activeHistoryId: nextActiveId } = reuseOrAppendHistory(
        latestData?.history,
        urls,
        (id) => ({
          id,
          url: urls[0] ?? url,
          ...(urls.length > 1 ? { urls } : {}),
          type: inferredType,
          lyrics,
          createdAt: nowIso,
          prompt: latestData?.instructions ?? '',
          modelId,
        }),
      );

      const nextGenerated = urls.length > 1
        ? { url: urls[0]!, urls, type: inferredType, lyrics }
        : { url: urls[0] ?? url, type: inferredType, lyrics };

      updateNodeData(nodeId, {
        status: 'completed',
        updatedAt: nowIso,
        model: modelId,
        generated: nextGenerated,
        history: nextHistory,
        activeHistoryId: nextActiveId,
        processingGenerationId: undefined,
        runtimeEstimatedTime: undefined,
        error: undefined,
      });
      toast.success(t('Audio generated successfully'));
      setTimeout(() => mutate(projectId ? `credits:${projectId}` : 'credits'), 5000);
    },
    onFailed: (error) => {
      const nowIso = new Date().toISOString();
      updateNodeData(nodeId, { status: 'failed', updatedAt: nowIso, processingGenerationId: undefined, runtimeEstimatedTime: undefined, error });
    },
  });
}
